import React, { useState, useEffect } from 'react';
import { Menu, X, UserCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom'; // Added Hooks
import logo from "../assets/images/ABN1.png";

const Navbar = ({ scrollToSection }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation(); // To detect current page
  const navigate = useNavigate(); // To redirect pages

  // Check if we are on the Home Page ("/")
  const isHomePage = location.pathname === '/';

  // Scroll detection logic
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // --- Unified Navigation Handler ---
  // Handles both scrolling on Home page AND redirecting from other pages
  const handleNavigation = (sectionId) => {
    if (isHomePage) {
      // If on Home page, just scroll smoothly
      scrollToSection(sectionId);
    } else {
      // If on another page, navigate to Home first
      // We pass the sectionId as state or hash to handle scrolling after redirect
      navigate('/', { state: { targetSection: sectionId } });
      
      // Fallback: Using hash if state handling is not set up in Home
      setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
              const headerHeight = 90;
              const elementPosition = element.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
              window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          }
      }, 100);
    }
    setIsMobileMenuOpen(false);
  };

  // --- Redirect Handler for Login ---
  const handleLogin = () => {
    window.location.href = '/member-login';
  };

  // --- Dynamic Styles Logic ---
  // If NOT Home page, force white background immediately.
  // If Home page, use scroll logic.
  const navbarClasses = !isHomePage || isScrolled
    ? 'bg-white shadow-md'
    : 'bg-white lg:bg-transparent lg:shadow-none';

  const textClasses = !isHomePage || isScrolled
    ? 'text-black hover:text-[#1A87CF]'
    : 'text-black lg:text-white hover:text-[#1A87CF] lg:hover:text-[#FB9724]';

  // Login button text color on Desktop
  const loginBtnDesktopClass = !isHomePage || isScrolled
    ? 'text-[#1A87CF] border-[#1A87CF] hover:bg-[#1A87CF] hover:text-white'
    : 'text-[#1A87CF] lg:text-white border-[#1A87CF] lg:border-white hover:bg-white hover:text-[#1A87CF]';

  const underlineClass = !isHomePage || isScrolled 
    ? 'bg-[#FB9724]' 
    : 'bg-[#FB9724] lg:bg-white';

  return (
    <header className={`fixed w-full max-w-[100vw] top-0 z-50 transition-all duration-300 ${navbarClasses}`}>
      <div className="container mx-auto px-4 sm:px-6">
        <nav className="flex items-center justify-between" style={{ height: '90px' }}>
          
          {/* Logo Section */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => handleNavigation('hero')}>
            <img 
              src={logo}
              alt="ABN Logo" 
              className="w-[110px] sm:w-[130px] h-auto object-contain" 
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-6">
            {/* Navigation Links */}
            {['About', 'Chapters', 'Activities', 'Members'].map((item) => (
              <button
                key={item}
                onClick={() => handleNavigation(item.toLowerCase())}
                className={`${textClasses} transition-colors font-medium relative group font-sans`}
              >
                {item === 'About' ? 'About Us' : item}
                <span className={`absolute bottom-0 left-0 w-0 h-[2px] ${underlineClass} transition-all duration-300 group-hover:w-full`}></span>
              </button>
            ))}

            {/* Desktop Action Buttons */}
            <div className="flex items-center gap-4 ml-4">
              
              {/* 1. Join Button (Now First) */}
              <button
                onClick={() => handleNavigation('join')}
                className="bg-[#FB9724] text-white px-6 py-2 rounded-full hover:bg-[#E0851F] hover:shadow-[0_5px_15px_rgba(251,151,36,0.3)] transition-all duration-300 font-sans border-none"
              >
                Join ABN
              </button>

              {/* 2. Login Button (Now Last) */}
              <button
                onClick={handleLogin}
                className={`border-2 px-5 py-2 rounded-full transition-all duration-300 font-sans font-medium flex items-center gap-2 ${loginBtnDesktopClass}`}
              >
                <UserCircle size={18} />
                Login
              </button>

            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-black transition-colors p-1"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </nav>

        {/* Mobile Menu Dropdown */}
        <div
          className={`lg:hidden bg-white shadow-lg absolute w-full left-0 right-0 transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-[450px] opacity-100 border-t border-gray-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="pb-6 px-4 flex flex-col space-y-3 pt-4">
            {/* Standard Links */}
            {['About', 'Chapters', 'Activities', 'Members'].map((item) => (
              <button
                key={item}
                onClick={() => handleNavigation(item.toLowerCase())}
                className="block w-full text-left text-gray-800 hover:text-[#1A87CF] py-3 px-4 rounded-xl hover:bg-blue-50 transition-colors font-sans font-medium text-lg border border-transparent hover:border-blue-100"
              >
                {item === 'About' ? 'About Us' : item}
              </button>
            ))}

            {/* Mobile Join Button */}
            <button
              onClick={() => handleNavigation('join')}
              className="block w-full text-center bg-[#FB9724] text-white py-3.5 rounded-xl hover:bg-[#E0851F] transition-colors font-sans font-semibold shadow-md mt-4 active:scale-95 transform duration-150"
            >
              Join ABN
            </button>

            {/* Mobile Login Button (Blue Color - Last Item) */}
            <button
              onClick={handleLogin}
              className="block w-full text-center bg-[#1A87CF] text-white py-3.5 rounded-xl hover:bg-[#1569A3] transition-colors font-sans font-semibold shadow-md flex items-center justify-center gap-2 active:scale-95 transform duration-150"
            >
              <UserCircle size={20} />
              Member Login
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;